import { Link } from 'react-router-dom'
import type { TShow } from '@schemas'
import { useShowStatsComparison } from '@/api/hooks'
import { Button } from '@/components/ui/button'

/**
 * Read-only: a show's previous edition is the member of its series that starts most recently
 * before it. `pending` means the series or start date has unsaved edits that could change it.
 */
export function DerivedPreviousEdition({ show, pending }: { show: TShow; pending: boolean }) {
  const series = show.seriesMembership?.series
  const query = useShowStatsComparison(show, Boolean(series))
  const data = query.data?.currentShow.id === show.id ? query.data : undefined
  return (
    <div className="space-y-1 text-sm">
      <p className="font-medium">Previous edition</p>
      {pending ? (
        <p role="status" className="text-muted-foreground">
          Save show settings to see the previous edition for these changes.
        </p>
      ) : !series ? (
        <p className="text-muted-foreground">
          None. A show is compared with the show in its series that starts most recently before it.
          Choose a series to compare this show with earlier ones.
        </p>
      ) : query.isError ? (
        <div role="alert" className="space-y-2">
          <p>Unable to check the previous edition.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </div>
      ) : !data ? (
        <p role="status" className="text-muted-foreground">
          Checking {series.name}…
        </p>
      ) : (
        <>
          <p>
            {data.comparisonState === 'available' && data.previousShow
              ? `${data.previousShow.name} (${data.previousShow.editionYear}), the show in ${series.name} that starts most recently before this one.`
              : data.comparisonState === 'unavailable'
                ? `An earlier show in ${series.name} is the previous edition, but it is not available to you.`
                : `None. This is the earliest show in ${series.name}.`}
          </p>
          <p className="text-muted-foreground">
            The series decides this from its shows and their start dates; change them in Series
            settings. Their classes are compared through{' '}
            <Link className="underline" to={`/show-series/${series.id}/alignment`}>
              Match classes across years
            </Link>
            .
          </p>
        </>
      )}
    </div>
  )
}
